import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

export interface PickOption {
  value: string;
  /** Plain text: what the search matches and what the closed box shows */
  label: string;
  /** Richer content for the list (an icon with the text, say); the label is used when absent */
  node?: React.ReactNode;
  disabled?: boolean;
  /** Options with the same group are listed under that heading */
  group?: string;
}

/** Text of React children, for searching and for the closed box */
export function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node)) return textOf((node.props as any)?.children);
  return "";
}

const TRIGGER =
  "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-2.5 text-left text-[13px] text-foreground outline-none transition-[color,box-shadow,border-color] hover:bg-muted/40 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive";

/**
 * A dropdown you can type into: it opens a list with a search box (typing on the closed box opens it with those letters).
 * Every dropdown in the CRM is one of these; `SearchableSelect` takes `<option>` children like a native select and
 * `ui/select` keeps the Select/SelectItem API, both on top of this.
 */
export function PickList({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  id,
  className,
  required,
  name,
  searchPlaceholder = "Search…",
  emptyText = "No matches",
  ...aria
}: {
  value: string;
  onChange: (value: string) => void;
  options: PickOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  required?: boolean;
  name?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  title?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const selected = options.find((o) => o.value === value);
  const groups = React.useMemo(() => {
    const out: Array<{ heading: string; items: Array<PickOption & { i: number }> }> = [];
    options.forEach((o, i) => {
      const heading = o.group || "";
      let g = out.find((x) => x.heading === heading);
      if (!g) out.push((g = { heading, items: [] }));
      g.items.push({ ...o, i });
    });
    return out;
  }, [options]);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-required={required || undefined}
            disabled={disabled}
            className={cn(TRIGGER, className)}
            onKeyDown={(e) => {
              // Typing on the closed box opens it and starts the search with that letter
              if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && e.key !== " ") {
                e.preventDefault();
                setQuery(e.key);
                setOpen(true);
              }
            }}
            {...aria}
          >
            <span className={cn("min-w-0 flex-1 truncate", !selected && "text-muted-foreground")}>{selected ? selected.node ?? selected.label : placeholder}</span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[12rem] max-w-[min(28rem,calc(100vw-2rem))] p-0">
          <Command
            filter={(_v, search, keywords) => ((keywords || []).join(" ").toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0)}
          >
            <CommandInput value={query} onValueChange={setQuery} placeholder={searchPlaceholder} autoFocus />
            <CommandList className="max-h-64">
              <CommandEmpty>{emptyText}</CommandEmpty>
              {groups.map((g) => (
                <CommandGroup key={g.heading || "_"} heading={g.heading || undefined}>
                  {g.items.map((o) => (
                    <CommandItem key={`${o.i}:${o.value}`} value={`opt-${o.i}`} keywords={[o.label, o.value]} disabled={o.disabled} onSelect={() => pick(o.value)} className="text-[13px]">
                      <Check className={cn("size-4 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{o.node ?? (o.label || " ")}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Keeps a form's "required" check and its field name working, as the native select did */}
      {(required || name) && (
        <input tabIndex={-1} aria-hidden="true" required={required} name={name} value={value ?? ""} onChange={() => undefined} className="pointer-events-none absolute h-px w-px opacity-0" />
      )}
    </>
  );
}

/** Options from `<option>` / `<optgroup>` children, however they were written (lists, conditionals, fragments) */
function optionsFromChildren(children: React.ReactNode, group?: string): PickOption[] {
  const out: PickOption[] = [];
  React.Children.toArray(children).forEach((child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as any;
    if (child.type === React.Fragment) out.push(...optionsFromChildren(props.children, group));
    else if (child.type === "optgroup") out.push(...optionsFromChildren(props.children, props.label));
    else if (child.type === "option") {
      if (props.hidden) return;
      const label = textOf(props.children);
      out.push({ value: props.value !== undefined ? String(props.value) : label, label, disabled: !!props.disabled, group });
    }
  });
  return out;
}

type NativeProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value" | "defaultValue"> & {
  value?: string | number | readonly string[];
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

/**
 * A searchable drop-in for `<select>`: same children (`<option>`), same `value` / `onChange(e)` with `e.target.value`,
 * same `className`, `id`, `disabled`, `required` and aria attributes.
 */
export function SearchableSelect({ value, defaultValue, onChange, children, disabled, id, className, required, name, title, ...rest }: NativeProps) {
  const options = optionsFromChildren(children);
  const [inner, setInner] = React.useState<string>(defaultValue !== undefined ? String(defaultValue) : options[0]?.value ?? "");
  const current = value !== undefined ? String(value) : inner;
  // A native select shows its first option when the value matches none
  const shown = options.some((o) => o.value === current) ? current : options[0]?.value ?? "";
  const emit = (v: string) => {
    if (value === undefined) setInner(v);
    const target = { value: v, name } as unknown as EventTarget & HTMLSelectElement;
    onChange?.({ target, currentTarget: target } as React.ChangeEvent<HTMLSelectElement>);
  };
  const aria = Object.fromEntries(Object.entries(rest).filter(([k]) => k.startsWith("aria-"))) as any;
  return (
    <PickList value={shown} onChange={emit} options={options} placeholder={options[0]?.label || "Select…"} disabled={disabled} id={id} className={className} required={required} name={name} title={title} {...aria} />
  );
}
